import { useState, memo, useMemo, useEffect } from 'react';
import dayjs from 'dayjs';
import { FaChevronDown, FaChevronUp, FaLock } from 'react-icons/fa';
import { resolveEventType, dotClassKey, entryLineClassKey, parseImpactReport } from '../../../utils/timelineUtils';
import { trackTimelineExpansion } from '../../../utils/modernUITracking';
import IncidentTimelineFilterMenu from './IncidentTimelineFilterMenu';
import IncidentTimelineDesktopFilter from './IncidentTimelineDesktopFilter';
import styles from './IncidentTimeline.module.css';
import { NOTE_SUB_TYPES } from './timelineFilterUtils';

const INITIAL_COUNT = 5;

/**
 * Determines the CSS class for a type pill based on event type.
 *
 * @param {string} type - The event type (communication, note, workflow)
 * @returns {string} CSS module class name
 */
function typePillClass(type) {
  switch (type) {
    case 'communication':
      return styles.typePillCommunication;
    case 'workflow':
      return styles.typePillWorkflow;
    case 'impact-report':
      return styles.typePillImpact;
    case 'note':
      return styles.typePillNote;
    default:
      return styles.typePillDefault;
  }
}

/**
 * Returns a human-readable label for the event type pill.
 *
 * @param {string} type - The event type
 * @returns {string} Display label
 */
function typeLabel(type) {
  switch (type) {
    case 'communication':
      return 'Communication';
    case 'workflow':
      return 'Workflow';
    case 'impact-report':
      return 'Impact Report';
    case 'note':
      return 'Note';
    default:
      return type || 'Event';
  }
}

/**
 * Extracts body text from an event based on its type.
 *
 * @param {Object} event - Timeline event
 * @returns {string} Body text to display
 */
function getBodyText(event) {
  const { eventType, data } = event;
  if (!data) return '';
  switch (eventType) {
    case 'communication':
      return data.messageForClients || data.subject || '';
    case 'note':
      return data.message || '';
    case 'workflow':
      return data.workflow?.label || '';
    default:
      return '';
  }
}

/**
 * Renders type-specific attribute badges for a note event.
 *
 * @param {Object} data - The event data payload
 * @returns {JSX.Element[]} Array of badge elements
 */
function renderNoteBadges(data, activeSubTypes) {
  const filterActive = activeSubTypes && activeSubTypes.size > 0;
  const show = (flag) => !filterActive || activeSubTypes.has(flag);
  const badges = [];
  if (data.important && show('important')) {
    badges.push(
      <span
        key="important"
        className={`${styles.attrBadge} ${styles.attrBadgeImportant}`}
      >
        Important
      </span>
    );
  }
  if (data.private && show('private')) {
    badges.push(
      <span
        key="private"
        className={`${styles.attrBadge} ${styles.attrBadgePrivate}`}
      >
        <FaLock aria-hidden="true" /> Private
      </span>
    );
  }
  if (data.pir && show('pir')) {
    badges.push(
      <span
        key="pir"
        className={`${styles.attrBadge} ${styles.attrBadgePir}`}
      >
        PIR
      </span>
    );
  }
  return badges;
}

/**
 * IncidentTimeline
 *
 * Renders a vertical stepper timeline of incident events (communications,
 * notes, workflows). Shows the first 5 entries by default with an expand
 * button to reveal the rest.
 *
 * Data comes via props — this component does NOT call any API.
 *
 * @param {Object}   props
 * @param {Array}    props.timelineEvents - Array of timeline event objects
 * @param {boolean}  props.loading        - Whether data is still loading
 * @param {string}   props.incidentId     - Problem number for tracking (US1947406)
 * @param {boolean}  props.isPastIncident - Whether this is a resolved incident (US1947406)
 * @param {Object}   props.newrelic       - NewRelic browser agent instance (US1947406)
 */
function IncidentTimeline({ timelineEvents, loading, incidentId, isPastIncident, newrelic }) {
  const [expanded, setExpanded] = useState(false);
  const [appliedTypes, setAppliedTypes] = useState(() => new Set());
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia?.('(min-width: 1024px)').matches === true
  );

  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia('(min-width: 1024px)');
    setIsDesktop(mq.matches);
    const onChange = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Filter out empty events per type rules
  const filteredEvents = useMemo(() => {
    if (!Array.isArray(timelineEvents)) return [];

    // Stable unique row key = base id + its OCCURRENCE index (0-based count of how
    // many prior rows shared that base id), NOT the position within filteredEvents.
    // This is both:
    //   - Unique: colliding/absent-id workflow rows become workflow-1700-0/-1/-2,
    //     so React never sees duplicate keys (the duplicate-key remount bug,
    //     US2044172).
    //   - Prepend-stable FOR UNIQUE-id rows only: a row whose id is unique is
    //     always its own 0th occurrence (note-a-0), so its key does NOT shift
    //     when a new event is prepended on background refresh — it keeps its DOM
    //     node instead of remounting. Rows that SHARE a baseId are not so lucky:
    //     the absent-id 'evt' fallback (evt-0/-1/...) and colliding same-id
    //     workflow rows (workflow-1700-0/-1/-2) are occurrence-numbered, so
    //     prepending another row with the same baseId shifts their numbers and
    //     remounts them. That is acceptable in practice: real timeline events
    //     always carry ids (notes/comms get UUIDs, workflows get
    //     workflow-{invocation}), so the 'evt' fallback is only a safety net and
    //     same-id collisions are rare and visually indistinguishable.
    // Because this useMemo depends on [timelineEvents] only (not appliedTypes), the
    // key stays stable across filter toggles and travels with the spread object
    // through displayEvents/visibleEvents.
    // Do NOT simplify to an id-only key: workflow events can share (or lack) an id,
    // and a bare id key causes React duplicate-key remounts (US2044172).
    // Honest caveat: for same-id collisions the occurrence number is stable only
    // while their relative order holds. backend/engine/timeline.go orders events
    // with a non-stable sort.Slice, so equal-timestamp workflows may reorder
    // between refreshes — acceptable here because those rows are visually
    // indistinguishable, and this is a frontend-only fix.
    const occ = new Map();
    return timelineEvents
      .filter((event) => {
        const { eventType, data } = event;
        if (!data) return true; // pass unknown shapes through
        switch (eventType) {
          case 'communication':
            return !!(data.messageForClients || data.subject);
          case 'note':
            return !!data.message;
          case 'workflow':
            return !!data.workflow?.label;
          default:
            return true;
        }
      })
      .map((event) => {
        const baseId = event.id ?? 'evt';
        const count = occ.get(baseId) ?? 0;
        occ.set(baseId, count + 1);
        return { ...event, _key: `${baseId}-${count}` };
      });
  }, [timelineEvents]);

  const availableTypes = useMemo(() => {
    const present = new Set();
    for (const ev of filteredEvents) {
      if (ev.eventType === 'communication') present.add('communication');
      if (ev.eventType === 'workflow') present.add('workflow');
      const resolved = resolveEventType(ev);
      if (resolved === 'impact-report') present.add('impact-report');
      if (resolved === 'note') present.add('note');
      if (ev.eventType === 'note' && ev.data?.important === true) present.add('important');
      if (ev.eventType === 'note' && ev.data?.private === true) present.add('private');
      if (ev.eventType === 'note' && ev.data?.pir === true) present.add('pir');
    }
    const ORDER = [
      { id: 'communication', label: 'Communication' },
      { id: 'workflow', label: 'Workflow' },
      { id: 'impact-report', label: 'Impact Report' },
      { id: 'note', label: 'Note' },
      { id: 'important', label: 'Important' },
      { id: 'private', label: 'Private' },
      { id: 'pir', label: 'PIR' },
    ];
    return ORDER.filter((t) => present.has(t.id));
  }, [filteredEvents]);

  const displayEvents = useMemo(() => {
    if (appliedTypes.size === 0) return filteredEvents;

    // OR across top-level categories.
    // Note sub-types (Important / Private / PIR): containment — show the note if it carries
    // the selected flag. Other flags on the note are irrelevant.
    const noteSubTypes = NOTE_SUB_TYPES.filter(t => appliedTypes.has(t));
    const hasNoteSubTypes = noteSubTypes.length > 0;

    return filteredEvents.filter((ev) => {
      const resolved = resolveEventType(ev);

      // Top-level categories — OR
      if (appliedTypes.has('communication') && ev.eventType === 'communication') return true;
      if (appliedTypes.has('workflow')       && ev.eventType === 'workflow')      return true;
      if (appliedTypes.has('impact-report')  && resolved    === 'impact-report')  return true;
      if (appliedTypes.has('note')           && resolved    === 'note')           return true;

      // Note sub-types — containment: show note if it carries any selected flag
      if (hasNoteSubTypes && ev.eventType === 'note') {
        return noteSubTypes.some((sub) => ev.data?.[sub] === true);
      }

      return false;
    });
  }, [filteredEvents, appliedTypes]);

  const activeSubTypes = useMemo(
    () => new Set(NOTE_SUB_TYPES.filter(t => appliedTypes.has(t))),
    [appliedTypes]
  );

  // Events render in prop order. Backend guarantees newest-first — no sort needed.
  const visibleEvents = expanded ? displayEvents : displayEvents.slice(0, INITIAL_COUNT);
  const hiddenCount = displayEvents.length - INITIAL_COUNT;
  const showExpandButton = displayEvents.length > INITIAL_COUNT;

  return (
    <div
      className={styles.timelineContainer}
      role={loading ? 'status' : undefined}
      aria-busy={loading || undefined}
    >
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Incident Timeline</h2>
        {!loading && filteredEvents.length > 0 && availableTypes.length > 0 && (
          <>
            {!isDesktop && (
              <IncidentTimelineFilterMenu
                availableTypes={availableTypes}
                appliedTypes={appliedTypes}
                onApply={setAppliedTypes}
              />
            )}
            {isDesktop && (
              <IncidentTimelineDesktopFilter
                availableTypes={availableTypes}
                appliedTypes={appliedTypes}
                onApply={setAppliedTypes}
              />
            )}
          </>
        )}
      </div>

      {loading && (
        <>
          <div className={styles.skeletonRow} />
          <div className={styles.skeletonRow} />
        </>
      )}

      {!loading && filteredEvents.length === 0 && (
        <p className={styles.emptyState}>No timeline events.</p>
      )}

      {!loading && filteredEvents.length > 0 && displayEvents.length === 0 && (
        <div className={styles.filterEmptyState}>
          No timeline entries match the selected filters.
          <button
            type="button"
            className={styles.filterResetLink}
            aria-label="Show all entries — clear active filters"
            onClick={() => setAppliedTypes(new Set())}
          >
            Show all entries
          </button>
        </div>
      )}

      {!loading && displayEvents.length > 0 && (
        <>
          <div className={styles.entriesList}>
            {visibleEvents.map((event) => {
              const resolvedType = resolveEventType(event);
              const body = getBodyText(event);
              const authorName =
                event.eventType === 'workflow'
                  ? null
                  : event.author?.name ||
                    (event.eventType === 'communication' ? event.data?.sender?.name : null) ||
                    null;
              const entryClass = entryLineClassKey(resolvedType);
              const impactParsed = resolvedType === 'impact-report'
                ? parseImpactReport(event.data?.message)
                : null;
              // Stable key: base id + occurrence index, prepend-stable for unique-id rows (see filteredEvents _key for full caveats). Never an id-only key — US2044172.
              return (
                <div className={[styles.timelineEntry, entryClass ? styles[entryClass] : null].filter(Boolean).join(' ')} key={event._key}>
                  <div className={[styles.timelineDot, styles[dotClassKey(resolvedType)]].filter(Boolean).join(' ')} aria-hidden="true" />
                  <div className={styles.entryContent}>
                    {/* Header row: date — [pill] [attr badges] — creator */}
                    <div className={styles.headerRow}>
                      <span className={styles.timestamp}>
                        {event.timestamp != null
                          ? dayjs(event.timestamp).format('MMM D, YYYY h:mm A')
                          : ''}
                      </span>
                      <span className={`${styles.typePill} ${typePillClass(resolvedType)}`}>
                        {typeLabel(resolvedType)}
                      </span>
                      {/* Note: Important / Private / PIR badges — inline, adjacent to type pill */}
                      {event.eventType === 'note' && event.data && renderNoteBadges(event.data, activeSubTypes)}
                      {authorName && (
                        <span className={styles.creatorName}>{authorName}</span>
                      )}
                    </div>

                    {/* Body text — structured for impact reports, raw for everything else */}
                    {impactParsed?.isImpactReport ? (
                      <div className={styles.impactSummary}>
                        {impactParsed.brand} - {impactParsed.application} • {impactParsed.status}
                      </div>
                    ) : (
                      body && <div className={styles.bodyText}>{body}</div>
                    )}

                    {/* Metadata rows */}
                    {event.eventType === 'communication' && event.data?.currentStatus && (
                      <div className={styles.metaRow}>Status: {event.data.currentStatus}</div>
                    )}

                    {event.eventType === 'workflow' && event.data?.agent && (
                      <div className={styles.metaRow}>Triggered by: {event.data.agent}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {showExpandButton && (
            <button
              type="button"
              className={styles.expandButton}
              onClick={() => {
                const next = !expanded;
                setExpanded(next);
                if (incidentId) {
                  trackTimelineExpansion(incidentId, {
                    totalEntries: displayEvents.length,
                    visibleEntries: INITIAL_COUNT,
                    expandedEntries: next ? displayEvents.length : INITIAL_COUNT,
                    action: next ? 'expand' : 'collapse',
                    section: isPastIncident ? 'past_incidents' : 'active_incidents',
                  }, newrelic);
                }
              }}
              aria-expanded={expanded}
              aria-label={
                expanded
                  ? 'Show less timeline entries'
                  : `Show ${hiddenCount} more ${hiddenCount === 1 ? 'entry' : 'entries'}`
              }
            >
              {expanded ? (
                <>
                  Show less
                  <FaChevronUp className={styles.expandIcon} aria-hidden="true" />
                </>
              ) : (
                <>
                  Show {hiddenCount} more {hiddenCount === 1 ? 'entry' : 'entries'}
                  <FaChevronDown className={styles.expandIcon} aria-hidden="true" />
                </>
              )}
            </button>
          )}
        </>
      )}
    </div>
  );
}

export default memo(IncidentTimeline);
